import type { ColumnDef } from '@tanstack/react-table';
import * as React from 'react';
import { Checkbox } from 'components/ui/checkbox';
import { DataTableColumnHeader as Header } from 'components/ui/DataTable';

const SELECTION_COLUMN_ID = 'Selection';
const SELECTION_COLUMN_SIZE = 48;

class DataTableHelper<TData = any> {
  public buildColumn<TAccessorKey = keyof TData>(
    props: ColumnDef<TData> & {
      id: string;
      accessorKey?: TAccessorKey;
      title?: React.ReactNode;
      content?: (row: TData) => React.ReactNode;
    },
  ): ColumnDef<TData> {
    const { id, accessorKey, title, content, ...defaultProps } = props;
    return {
      id,
      ...(accessorKey && {
        accessorKey: accessorKey as string,
      }),
      header: ({ column }) => <Header column={column} title={title ?? id} />,
      ...(content && {
        cell: ({ row }) => content(row.original),
      }),
      ...defaultProps,
    };
  }

  public buildSelectionByRowColumn(props?: {
    size?: number
  }): ColumnDef<TData> {
    return {
      id: SELECTION_COLUMN_ID,
      size: props?.size ?? SELECTION_COLUMN_SIZE,
      cell: ({ row }) => (
        <Checkbox
          onClick={e => e.stopPropagation()}
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      ),
      enableHiding: false,
      enableSorting: false,
      ...props,
    };
  }

  public buildSelectionByPageColumn(props?: {
    size?: number
  }): ColumnDef<TData> {
    return {
      id: SELECTION_COLUMN_ID,
      size: props?.size ?? SELECTION_COLUMN_SIZE,
      header: ({ table }) => (
        <Checkbox
          onClick={e => e.stopPropagation()}
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && 'indeterminate')
          }
          onCheckedChange={(value) =>
            table.toggleAllPageRowsSelected(!!value)
          }
          aria-label="Select all rows on page"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          onClick={e => e.stopPropagation()}
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      ),
      enableHiding: false,
      enableSorting: false,
      ...props,
    };
  }

  public buildSelectionByTableColumn(props?: {
    size?: number
  }): ColumnDef<TData> {
    return {
      id: SELECTION_COLUMN_ID,
      size: props?.size ?? SELECTION_COLUMN_SIZE,
      header: ({ table }) => (
        <Checkbox
          onClick={e => e.stopPropagation()}
          checked={
            table.getIsAllRowsSelected() ||
            (table.getIsSomeRowsSelected() && 'indeterminate')
          }
          onCheckedChange={(value) => table.toggleAllRowsSelected(!!value)}
          aria-label="Select all rows"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          onClick={e => e.stopPropagation()}
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      ),
      enableHiding: false,
      enableSorting: false,
    };
  }
}

export { DataTableHelper };
